'use client';

/**
 * AITaskPanel - Floating panel showing AI task status.
 * 
 * Accessible from the header via a Bot icon button.
 * Shows running/completed/failed tasks with real-time progress.
 */

import {
  Bot,
  Loader2,
  CheckCircle2,
  XCircle,
  X,
  Trash2,
  Clock,
  ChevronRight,
  History,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useAITaskStore, type AITask } from '@/stores/ai-task-store';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useState } from 'react';

export function AITaskButton() {
  const { unreadCount, togglePanel, panelOpen, activeTaskIds } = useAITaskStore();
  const hasActive = activeTaskIds.size > 0;

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={togglePanel}
      className={cn(
        'relative',
        panelOpen && 'bg-muted',
        hasActive && 'text-primary'
      )}
    >
      <Bot className={cn('h-5 w-5', hasActive && 'animate-pulse')} />
      {unreadCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </Button>
  );
}

export function AITaskPanel() {
  const {
    panelOpen,
    setPanelOpen,
    getTaskList,
    removeTask,
    activeTaskIds,
  } = useAITaskStore();

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const tasks = getTaskList();

  if (!panelOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        onClick={() => setPanelOpen(false)}
      />

      {/* Panel */}
      <div className="fixed right-4 top-16 z-50 w-96 max-h-[calc(100vh-5rem)] bg-card border border-border rounded-xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-top-2 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Bot className="h-4 w-4 text-primary" />
            <span className="font-medium text-sm">AI 任务</span>
            {activeTaskIds.size > 0 && (
              <Badge variant="secondary" className="text-xs px-1.5 py-0">
                {activeTaskIds.size} 进行中
              </Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setPanelOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Task list */}
        <div className="flex-1 overflow-y-auto">
          {tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <History className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">暂无 AI 任务</p>
              <p className="text-xs mt-1">使用 AI 分析时，任务会显示在这里</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {tasks.map((task) => (
                <TaskItem
                  key={task.id}
                  task={task}
                  expanded={expandedId === task.id}
                  onToggle={() => setExpandedId(expandedId === task.id ? null : task.id)}
                  onDelete={() => {
                    removeTask(task.id);
                    // Also delete from server
                    fetch(`/api/v1/ai-chat/tasks/${task.id}`, { method: 'DELETE' }).catch(() => {});
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function getDisplayContent(task: AITask): string {
  if (task.source === 'smart-picker-v2' && task.content) {
    try {
      const parsed = JSON.parse(task.content);
      return parsed.ai_analysis || 'V2 分析已完成，综合研判结果已更新到主界面。';
    } catch {
      return task.content;
    }
  }
  return task.content || '';
}

function TaskItem({
  task,
  expanded,
  onToggle,
  onDelete,
}: {
  task: AITask;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const statusConfig = {
    pending: { icon: Clock, color: 'text-muted-foreground', label: '等待中' },
    running: { icon: Loader2, color: 'text-blue-500', label: '运行中', animate: true },
    streaming: { icon: Loader2, color: 'text-primary', label: '生成中', animate: true },
    completed: { icon: CheckCircle2, color: 'text-green-500', label: '已完成' },
    failed: { icon: XCircle, color: 'text-destructive', label: '失败' },
  };

  const config = statusConfig[task.status] || statusConfig.pending;
  const StatusIcon = config.icon;
  const displayContent = getDisplayContent(task);

  return (
    <div className="group">
      <button
        className="w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors flex items-start gap-3"
        onClick={onToggle}
      >
        <StatusIcon
          className={cn(
            'h-4 w-4 mt-0.5 shrink-0',
            config.color,
            'animate' in config && config.animate && 'animate-spin'
          )}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">{task.title}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={cn('text-xs', config.color)}>{config.label}</span>
            {task.providerUsed && (
              <Badge variant="outline" className="text-[10px] px-1 py-0">
                {task.providerUsed}
              </Badge>
            )}
            {task.latencyMs && (
              <span className="text-xs text-muted-foreground">
                {(task.latencyMs / 1000).toFixed(1)}s
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {_formatTime(task.createdAt)}
            </span>
          </div>

          {/* Streaming preview */}
          {task.status === 'streaming' && displayContent && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {displayContent.slice(-100)}
              <span className="inline-block w-1.5 h-3 bg-primary/70 animate-pulse ml-0.5 align-text-bottom" />
            </p>
          )}

          {/* Error */}
          {task.status === 'failed' && task.error && (
            <p className="text-xs text-destructive mt-1 line-clamp-2">{task.error}</p>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <ChevronRight
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              expanded && 'rotate-90'
            )}
          />
        </div>
      </button>

      {/* Expanded content */}
      {expanded && displayContent && (
        <div className="px-4 pb-3">
          <div className="bg-muted/30 rounded-lg p-3 max-h-60 overflow-y-auto">
            <div className="prose prose-sm dark:prose-invert max-w-none text-xs">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {displayContent}
              </ReactMarkdown>
              {task.status === 'streaming' && (
                <span className="inline-block w-1.5 h-3 bg-primary/70 animate-pulse ml-0.5 align-text-bottom" />
              )}
            </div>
          </div>
          <div className="flex justify-end mt-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground hover:text-destructive gap-1 h-7"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <Trash2 className="h-3 w-3" />
              删除
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function _formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = now.getTime() - d.getTime();

    if (diff < 60000) return '刚刚';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
    return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch {
    return '';
  }
}

export default AITaskPanel;
